import React, { useState, useRef, useCallback, useEffect } from "react";
import { AiOutlineLoading, AiOutlineClose } from "react-icons/ai";
import { FiMapPin } from "react-icons/fi";

import styles from "./styles.module.css";
import { CitySearchInputProps, CitySearchResult } from "../../hooks/types";
import { useCitySearch } from "../../hooks/useCitySearch";

interface SelectWithLiveSearchProps extends CitySearchInputProps {
  label?: string;
  id?: string;
}

export const SelectWithLiveSearch: React.FC<SelectWithLiveSearchProps> = ({
  name,
  placeholder = "Digite o nome da cidade...",
  onCitySelect,
  className = "",
  label,
  id,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [_selectedCity, setSelectedCity] = useState<CitySearchResult | null>(
    null
  );

  const { isLoading, results, searchCities, clearResults } = useCitySearch();

  const handleInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setInputValue(value);

      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = value;
      }

      if (value.trim().length >= 2) {
        await searchCities(value);
        setIsOptionsVisible(true);
      } else {
        clearResults();
        setIsOptionsVisible(false);
      }
    },
    [searchCities, clearResults]
  );

  const handleCitySelect = useCallback(
    (city: CitySearchResult) => {
      setSelectedCity(city);
      setInputValue(city.fullName);
      setIsOptionsVisible(false);
      clearResults();

      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = city.fullName;
      }

      if (onCitySelect) {
        onCitySelect(city);
      }
    },
    [onCitySelect, clearResults]
  );

  const handleClearInput = useCallback(() => {
    setInputValue("");
    setSelectedCity(null);
    setIsOptionsVisible(false);
    clearResults();

    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = "";
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [clearResults]);

  const handleInputFocus = useCallback(() => {
    if (inputValue.trim().length >= 2 && results.length > 0) {
      setIsOptionsVisible(true);
    }
  }, [inputValue, results.length]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOptionsVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div className={`${styles.container} ${className}`}>
      {label && (
        <h3 className={styles.label} id={id}>
          {label}
        </h3>
      )}

      <div ref={containerRef} className={styles.selectContainer}>
        <input
          ref={hiddenInputRef}
          type="hidden"
          name={name}
          value={inputValue}
        />

        <div className={styles.inputContainer}>
          <FiMapPin className={styles.icon} />

          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            placeholder={placeholder}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            className={styles.input}
            id={id}
          />

          {isLoading && (
            <AiOutlineLoading
              className={`${styles.actionIcon} ${styles.loading}`}
            />
          )}

          {inputValue && !isLoading && (
            <AiOutlineClose
              className={`${styles.actionIcon} ${styles.clear}`}
              onClick={handleClearInput}
            />
          )}
        </div>

        {isOptionsVisible && (
          <div className={styles.options}>
            {results.length > 0 ? (
              results.map((city, index) => (
                <button
                  key={`${city.city}-${city.state}-${index}`}
                  type="button"
                  className={styles.option}
                  onClick={() => handleCitySelect(city)}
                >
                  <span className={styles.cityName}>{city.city}</span>
                  <span className={styles.stateName}>{city.state}</span>
                </button>
              ))
            ) : (
              <div className={styles.emptyOptions}>Nenhuma cidade encontrada</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
